import _ from 'underscore';
import Localizer from 'localizer';
import Backbone from 'backbone';
import ViewWrapper from 'views/utils/ViewWrapper';
import GdprModalView from 'views/gdpr/GdprModalView';
import GarminDeviceUploadModal from 'pages/settings/views/GarminDeviceUploadModal';
import DataStorageConsentModal from 'pages/settings/views/DataStorageConsentModal';
import UploadDataConsentModal from 'pages/settings/views/UploadDataConsentModal';
import GrantConsent from 'models/gdpr/consent/GrantConsent';
import RevokeConsentWithToken from 'models/gdpr/consent/RevokeConsentWithToken';
import CheckPasswordWithToken from 'models/gdpr/CheckPasswordWithToken';
import ModalPopupView from 'views/main/Modal/ModalPopupView';
import GdprUtil from 'utils/gdpr/GdprUtil';
import ModalStack from 'utils/ModalStack';
import GdprModalDeleteConfirmation from 'views/gdpr/GdprModalDeleteConfirmation.js';
import GdprModalWithSwitch from 'views/gdpr/GdprModalWithSwitch';
import GdprConfirmationModalView from './GdprConfirmationModalView';
import LocaleMapper from 'utils/LocaleMapper';
import ViewerUserPreferences from 'models/user/ViewerUserPreferences';
import UserPreference from 'models/user/UserPreference';
import ModelSynchronizer from 'utils/ModelSynchronizer';
import ReactComponentView from 'views/main/react/ReactComponentView';
import ComponentForUpdatingStoreFromBackbone from 'components/Utils/ComponentForUpdatingStoreFromBackbone';
import { setGdprLimitedFeaturesAlert } from 'reducers/gdpr/gdprDisplayLimitedFeatureAlert';

export default Backbone.View.extend({
    initialize: function(args) {
        this.accountDeviceSyncPref = args.accountDeviceSyncPref;
        this.gdprFeatureUpload = args.gdprFeatureUpload;
        this.isUserOptIn = args.isUserOptIn;
        this.activeATPs = args.activeATPs;

        // These are callback methods used for modal buttons
        this.onAccountDeviceSynced = args.onAccountDeviceSynced;
        this.onUploadRevoked = args.onUploadRevoked;
        this.onUploadConsented = args.onUploadConsented;
        this.onCloseButtonClicked = args.onCloseButtonClicked;
        this.onCancelUploadDataModalView = args.onCancelUploadDataModalView;

        this.onAllowDeviceToUploadData = args.onAllowDeviceToUploadData;
        this.navigateToPreviousPage = args.navigateToPreviousPage;
        this.options = args.options || {};
        this.language = LocaleMapper.getFullLocale(ViewerUserPreferences.get('preferredLocale'));
    },

    // This method is the starting point for the granting and revoking of a user's upload consent, many other methods lead here in order to open the upload consent modal
    // > upload consent is aka "Storage & Processing" consent, it is the primary type of GDPR consent for Garmin Connect
    gdprUpload: function(rcdOption) {
        const deleteStatus = this.gdprFeatureUpload.get('deleteStatus');

        // If a data deletion is in progress or is about to be in progress(i.e. confirmed)), show a modal informing the user and tell them to try again later
        if (deleteStatus === GdprUtil.DELETE_STATUS.IN_PROGRESS || deleteStatus === GdprUtil.DELETE_STATUS.CONFIRMED) {
            this.dataDeleteInProgress();
        }

        // The rcdOption is used to force the revoke flow when called from showRcdWarningModal() since either the opt-in or opt-out flow can be started from there
        // > if the user was already opted out, the else if(this.isGdprFeatureOn(this.gdprFeatureUpload)) logic would cause the revoke flow to be skipped
        else if (this.isGdprFeatureOn(this.gdprFeatureUpload) || rcdOption === GdprUtil.RCD_OPTIONS.DELETE) {
            this.startUploadRevokeFlow(
                'revoke',
                Localizer.localize('data_and_privacy'),
                GdprUtil.CONSENT_TYPE_UPLOAD,
                this.onUploadRevoked && this.onUploadRevoked.success && this.onUploadRevoked.success.bind(this),
                this.onUploadRevoked && this.onUploadRevoked.error.bind(this)
            );
        } else {
            this.openGdprModal(
                'grant',
                Localizer.localize('data_and_privacy'),
                GdprUtil.CONSENT_TYPE_UPLOAD,
                (method, model) => {
                    if (
                        model.consentState === 'GRANTED' &&
                        !(
                            this.accountDeviceSyncPref &&
                            _.isFunction(this.accountDeviceSyncPref.equalsIgnoreCase) &&
                            this.accountDeviceSyncPref.equalsIgnoreCase('true')
                        )
                    ) {
                        this.checkDeviceSyncPreference();
                    }
                    if (model.consentState === 'REVOKED') {
                        this.onUploadRevoked && this.onUploadRevoked.success && this.onUploadRevoked.success();
                    } else {
                        this.onUploadConsented && this.onUploadConsented.success && this.onUploadConsented.success();
                    }
                },
                this.onUploadConsented && this.onUploadConsented.error.bind(this),
                rcdOption
            );
        }
    },

    gdprUploadWithoutAccountDeviceSyncPref: function(userGrantedActionCallback, userRevokeActionCallback) {
        this.openGdprModal(
            'grant',
            Localizer.localize('data_and_privacy'),
            GdprUtil.CONSENT_TYPE_UPLOAD,
            (method, model) => {
                if (model.consentState === 'GRANTED') {
                    userGrantedActionCallback && userGrantedActionCallback();
                } else if (model.consentState === 'REVOKED') {
                    userRevokeActionCallback && userRevokeActionCallback();
                }
            },
            this.onUploadConsented && this.onUploadConsented.error.bind(this)
        );
    },

    checkDeviceSyncPreference: function() {
        const modelSynchronizer = new ModelSynchronizer();

        this.accountDeviceSyncPref = new UserPreference({
            key: 'account.deviceSync'
        });

        modelSynchronizer.addModel({ model: this.accountDeviceSyncPref, required: true });
        modelSynchronizer.on(
            ModelSynchronizer.Events.SYNCHRONIZED,
            () => {
                // Only ask for the user to enable device upload if they are opted out of it
                if (
                    this.accountDeviceSyncPref &&
                    _.isFunction(this.accountDeviceSyncPref.equalsIgnoreCase) &&
                    this.accountDeviceSyncPref.equalsIgnoreCase('false')
                ) {
                    this.handleGarminDeviceUpload();
                }
            },
            this
        );
        modelSynchronizer.fetchModels();
    },

    handleGarminDeviceUpload: function(failCallback) {
        if (this.enableFromDeviceUpload) {
            // start from Garmin Device Upload, need to render the modal again
            this.updateAccountDeviceSync(
                'true',
                () => {
                    this.renderGarminDeviceUploadModal();
                },
                failCallback
            );
        } else {
            // ask user to turn on Garmin Device Upload after finishing Data Storage
            this.renderUploadDataModal({
                onAccept: () => {
                    this.updateAccountDeviceSync(
                        'true',
                        () => this.onAllowDeviceToUploadData && this.onAllowDeviceToUploadData(),
                        failCallback
                    );
                }
            });
        }
    },

    renderGarminDeviceUploadModal: function() {
        this.enableFromDeviceUpload = false;
        this.garminDeviceUploadModalView = this.getGarminDeviceUploadModalView();
        this.renderDeviceUploadModal(this.garminDeviceUploadModalView);
    },

    getGarminDeviceUploadModalView: function() {
        return new GarminDeviceUploadModal({
            accountDeviceSyncPref: this.accountDeviceSyncPref,
            onDeviceSyncToggle: (deviceSync) => {
                this.accountDeviceSync = deviceSync;
                if (!this.accountDeviceSync) {
                    // user turns Garmin Device Upload OFF
                    this.updateAccountDeviceSync('false');
                } else {
                    // user turns Garmin Device Upload ON
                    // if Data Storage is ON, update Garmin Device Upload
                    // otherwise, ask user to turn Data Storage ON
                    if (this.isGdprFeatureOn(this.gdprFeatureUpload)) {
                        this.updateAccountDeviceSync('true');
                    } else {
                        this.renderDataStorageConsentModal();
                    }
                }
            }
        });
    },

    renderDataStorageConsentModal: function() {
        this.dataStorageConsentModal = new DataStorageConsentModal({
            onAccept: () => {
                this.enableFromDeviceUpload = true;
                this.gdprUpload();
            }
        });
        this.renderDeviceUploadModal(this.dataStorageConsentModal);
    },

    renderDeviceUploadModal: function(view) {
        const deleteStatus = this.gdprFeatureUpload.get('deleteStatus');

        // If a data deletion is in progress or is about to be in progress(i.e. confirmed)), show a modal informing the user and tell them to try again later
        if (deleteStatus === GdprUtil.DELETE_STATUS.IN_PROGRESS || deleteStatus === GdprUtil.DELETE_STATUS.CONFIRMED) {
            this.dataDeleteInProgress();
            return;
        } else if (this.deviceUploadModalViewWrapper) {
            this.deviceUploadModalViewWrapper.setView(view);
            this.deviceUploadModalViewWrapper.render();
            return;
        }

        // Use ViewWrapper to be able to swap the view on Modal without opening new one
        this.deviceUploadModalViewWrapper = new ViewWrapper();
        this.deviceUploadModalViewWrapper.setView(view);

        this.dataPrivacyModalView = new ModalPopupView({
            modalTitle: Localizer.localize('data_and_privacy'),
            view: this.deviceUploadModalViewWrapper,
            useCustomBody: true,
            showFooter: false,
            closable: false
        });
        this.dataPrivacyModalView.render();

        this.dataPrivacyModalView.on(ModalPopupView.Events.ModalPopupClosed, () => {
            if (this.deviceUploadModalViewWrapper) {
                this.deviceUploadModalViewWrapper.remove();
                this.deviceUploadModalViewWrapper = null;
            }
        });
    },

    renderUploadDataModal: function(args) {
        this.uploadDataConsentModal = new UploadDataConsentModal();
        this.uploadDataModalView = new ModalPopupView({
            modalTitle: Localizer.localize('data_and_privacy'),
            view: this.uploadDataConsentModal,
            closable: false,
            cancelTextKey: 'not_now',
            confirmationTextKey: 'allow',
            onCloseButtonClicked: () => {
                if (this.onCancelUploadDataModalView) {
                    this.onCancelUploadDataModalView();
                }
            }
        });
        this.uploadDataModalView.on(ModalPopupView.Events.CANCEL_CLICKED, () => {
            if (this.onCancelUploadDataModalView) {
                this.onCancelUploadDataModalView();
            } else {
                this.uploadDataModalView.close();
            }
        });
        this.uploadDataModalView.render();
        this.uploadDataModalView.on(ModalPopupView.Events.CONFIRM_CLICKED, () => {
            if (args && args.onAccept) {
                args.onAccept();
            }
        });
    },

    updateAccountDeviceSync: function(value, successCallback, errorCallback) {
        if (!this.accountDeviceSyncPref) {
            this.accountDeviceSyncPref = new UserPreference({
                key: 'account.deviceSync'
            });
        } else if (this.accountDeviceSyncPref.getValue() === value) {
            // value is not changed, do nothing
            return;
        }

        this.accountDeviceSyncPref.setValue(value);
        this.accountDeviceSyncPref.save(null, {
            success: () => {
                successCallback && successCallback();
                this.onAccountDeviceSynced && this.onAccountDeviceSynced.success();
            },
            error: () => {
                errorCallback && errorCallback();
                this.onAccountDeviceSynced && this.onAccountDeviceSynced.error();
            }
        });
    },

    // Note: be careful using this method, if gdprFeature isn't passed in and isUserOptIn wasn't supplied to this view, this will return true no matter what
    // > if this.isUserOptIn is undefined, returning !this.isUserOptIn equals true
    isGdprFeatureOn: function(gdprFeature) {
        return gdprFeature
            ? GdprUtil.isFeatureForUserOn(
                  gdprFeature.get('userOption'),
                  gdprFeature.get('consentEnforcementState'),
                  gdprFeature.get('sourceType')
              )
            : !this.isUserOptIn;
    },

    // Sets up the series of modals shown to a user who wants to revoke their GDPR upload consent
    startUploadRevokeFlow: function(grantOrRevoke, modalTopTitle, consentTypeId, syncCallback, errorCallback) {
        let modals = [];
        let modalStack = new ModalStack();

        modals.push(
            this.uploadRevokeFirstModal(grantOrRevoke, modalTopTitle, consentTypeId),
            this.uploadRevokeSecondModal(modalTopTitle, consentTypeId, syncCallback, errorCallback)
        );

        modalStack.addModals(modals);
        modalStack.iterate();
    },

    // This is the "Are you sure" modal
    openGdprConfirmationModal: function(consentTypeId, syncCallback, errorCallback) {
        let modalBody = Localizer.localize('revoke_consent_description_version_two');

        // Index scale flow, /modern/device/primary-get-started, has a different text than the normal flow
        if ('index-scale' === this.options.source) {
            modalBody = Localizer.localize('index_scale_revoke_consent_description');
        }

        let gdprConfirmationModalView = new GdprConfirmationModalView({
            modalTopTitle: Localizer.localize('data_and_privacy'),
            modalTitle: Localizer.localize('revoke_consent_title'),
            modalBody: modalBody,
            showFooter: false,
            saveBtn: Localizer.localize('button_continue'),
            cancelBtn: Localizer.localize('button_cancel')
        });

        let modalPopupView = new ModalPopupView({
            view: gdprConfirmationModalView,
            closable: false,
            showHeader: false,
            useCustomBody: true,
            showFooter: false,
            // "Continue" button
            onSaveButtonClicked: () => {
                modalPopupView.close(() => {
                    this.revokeConsent(
                        consentTypeId,
                        () => {
                            // If Data Storage is not consented, Garmin Device Upload will be disable
                            this.updateAccountDeviceSync('false');

                            if (!!this.navigateToPreviousPage) {
                                Backbone.history.history.back();
                            }
                        },
                        errorCallback
                    );
                });
            },
            // "X" button
            onCloseButtonClicked: () => {
                modalPopupView.close(() => {
                    this.onGdprConfirmationModalCancel();
                });
            }
        });

        // "Cancel" button
        modalPopupView.on(ModalPopupView.Events.CANCEL_CLICKED, () => {
            this.onGdprConfirmationModalCancel();
        });

        return modalPopupView.render();
    },

    onGdprConfirmationModalCancel: function() {
        this.openGdprModal(
            'grant',
            Localizer.localize('data_and_privacy'),
            GdprUtil.CONSENT_TYPE_UPLOAD,
            (method, model) => {
                if (
                    model.consentState === 'GRANTED' &&
                    !(
                        this.accountDeviceSyncPref &&
                        _.isFunction(this.accountDeviceSyncPref.equalsIgnoreCase) &&
                        this.accountDeviceSyncPref.equalsIgnoreCase('true')
                    )
                ) {
                    this.handleGarminDeviceUpload();
                }
                if (model.consentState === 'REVOKED') {
                    this.onUploadRevoked && this.onUploadRevoked.success && this.onUploadRevoked.success();
                } else {
                    this.onUploadConsented && this.onUploadConsented.success && this.onUploadConsented.success();
                }
            },
            this.onUploadConsented && this.onUploadConsented.error
        );
    },

    // Sets a flag in the global store that's used by the GdprLimitedFeatureAlert to show or hide when consent is revoked or granted
    setReactGdprStoreValue: function(consentType) {
        this.gdprEmptyComponent = new ReactComponentView();
        this.gdprEmptyComponent.renderComponent(
            ComponentForUpdatingStoreFromBackbone,
            // empty jQuery element on mainsidebarview
            jQuery('.empty-for-empty-react-component')[0],
            {
                actionToTrigger: setGdprLimitedFeaturesAlert,
                param: consentType
            }
        );
    },

    // Opens the GDPR consent modal
    // > used for upload, Insights, and ATP consent
    openGdprModal: function(grantOrRevoke, modalTopTitle, consentTypeId, syncCallback, errorCallback, rcdOption) {
        const deleteStatus = this.gdprFeatureUpload.get('deleteStatus');

        // If a data deletion is in progress or is about to be in progress(i.e. confirmed)), show a modal informing the user and tell them to try again later
        if (deleteStatus === GdprUtil.DELETE_STATUS.IN_PROGRESS || deleteStatus === GdprUtil.DELETE_STATUS.CONFIRMED) {
            this.dataDeleteInProgress();
            return;
        }

        this.consentModalPopupView = this.setGdprModalView(grantOrRevoke, modalTopTitle, consentTypeId);

        let modalPopupView = new ModalPopupView({
            view: this.consentModalPopupView,
            showHeader: false,
            useCustomBody: true,
            showFooter: false,
            closable: false,
            // "Agree" and "Opt Out" buttons
            onSaveButtonClicked: () => {
                modalPopupView.close(() => {
                    if (grantOrRevoke === 'grant') {
                        this.grantConsent(consentTypeId, syncCallback, errorCallback);
                    } else if (grantOrRevoke === 'revoke') {
                        this.revokeConsent(consentTypeId, syncCallback, errorCallback);
                    }
                    this.setReactGdprStoreValue(grantOrRevoke === 'revoke');
                });
            },
            // "X" button
            onCloseButtonClicked: () => {
                modalPopupView.close(() => {
                    if (this.returnToRcdWarningModal()) {
                        this.showRcdWarningModal();
                    } else if (this.onCloseButtonClicked) {
                        this.onCloseButtonClicked();
                    }
                });
            }
        });

        if (grantOrRevoke === 'grant') {
            // "Do Not Agree" and "Cancel" buttons
            modalPopupView.on(ModalPopupView.Events.CANCEL_CLICKED, () => {
                if (consentTypeId === GdprUtil.CONSENT_TYPE_UPLOAD) {
                    // In this situation, send the user back to the RCD warning modal if it was previously shown, they chose to keep their data, then changed their mind and clicked the "Do Not Agree" button
                    // > when their deleteStatus is NOT_CONFIRMED, LOCATION_CHANGED, or PASSWORD_CHECK_FAILED we need to force them into a choice to keep or delete their data
                    if (rcdOption && this.gdprRcdCheck()) {
                        this.showRcdWarningModal();
                    } else {
                        // "Are you sure" modal
                        this.openGdprConfirmationModal(consentTypeId, syncCallback, errorCallback);
                    }
                } else {
                    this.revokeConsent(consentTypeId, syncCallback, errorCallback);
                }
            });
        } else if (grantOrRevoke === 'revoke') {
            // "Do Not Agree" and "Cancel" buttons
            modalPopupView.on(ModalPopupView.Events.CANCEL_CLICKED, this.close, this);
        }

        modalPopupView.render();
    },

    // Opens a GDPR consent modal with a toggle switch
    // > used for product improvement consent
    openGdprModalWithSwitch: function(isFeatureAccepted, modalTopTitle, consentTypeId, syncCallback, errorCallback) {
        this.consentModalPopupView = this.setGdprModalWithSwitch(isFeatureAccepted, modalTopTitle, consentTypeId);
        let modalPopupView = new ModalPopupView({
            view: this.consentModalPopupView,
            showHeader: false,
            useCustomBody: true,
            showFooter: false,
            closable: false,
            // The toggle switch
            onSaveButtonClicked: () => {
                modalPopupView.close(() => {
                    if (
                        this.consentModalPopupView.isSwitchChecked() !==
                        this.consentModalPopupView.isSwitchInitiallyChecked()
                    ) {
                        if (this.consentModalPopupView.isSwitchChecked()) {
                            this.grantConsent(consentTypeId, syncCallback, errorCallback);
                        } else {
                            this.revokeConsent(consentTypeId, syncCallback, errorCallback);
                        }
                    }
                });
            },
            // "X" button
            onCloseButtonClicked: () => {
                if (this.onCloseButtonClicked) {
                    this.onCloseButtonClicked();
                }
            }
        });

        modalPopupView.render();
    },

    setGdprModalView: function(grantOrRevoke, modalTopTitle, consentTypeId) {
        return new GdprModalView({
            consentType: consentTypeId,
            grantRevoke: grantOrRevoke,
            modalTopTitle: modalTopTitle
        });
    },

    setGdprModalWithSwitch: function(isFeatureAccepted, modalTopTitle, consentTypeId) {
        return new GdprModalWithSwitch({
            consentType: consentTypeId,
            isFeatureAccepted: isFeatureAccepted,
            modalTopTitle: modalTopTitle
        });
    },

    grantConsent: function(consentTypeId, syncCallback, errorCallback) {
        let consentVersion = this.consentModalPopupView.getConsentVersion(),
            grantConsent = new GrantConsent({
                consentTypeId: consentTypeId,
                consentLocale: this.language,
                consentVersion: consentVersion
            });

        grantConsent.bind('sync', syncCallback, this);
        grantConsent.bind('error', errorCallback, this);
        grantConsent.save();
    },

    // clientIndicator is a boolean to indicate if the user was presented with the password checkpoint or not
    revokeConsent: function(
        consentTypeId,
        syncCallback,
        errorCallback,
        clientIndicator = false,
        passwordToken = null,
        authorizationFail = null
    ) {
        const consentVersion = this.consentModalPopupView.getConsentVersion();
        const revokeConsentWithToken = new RevokeConsentWithToken({
            consentTypeId: consentTypeId,
            clientIndicator: clientIndicator,
            consentLocale: this.language,
            consentVersion: consentVersion
        });

        revokeConsentWithToken.bind('sync', syncCallback, this);
        revokeConsentWithToken.bind(
            'error',
            (model, resp) => {
                // Revoke call was made but the API requires authorization to revoke (http status of 406)
                if (resp.status === 406) {
                    // Revoke call came from the password flow, fire authorizationFail() to show the error message on the password input modal
                    if (clientIndicator && typeof authorizationFail === 'function') {
                        authorizationFail();
                    }
                    // Revoke call came from another flow, open the RCD Warning modal to confirm they want to keep or delete their data, this will put the user into the password flow if they choose to delete
                    else {
                        this.rcdWarningModalShownDueTo406 = true;
                        this.showRcdWarningModal();
                    }
                }
                // Handle any other errors
                else {
                    if (typeof errorCallback === 'function') {
                        errorCallback();
                    } else {
                        this.gdprUpload(); // If there was a network or server error, and no specific error handler provided, return the user to the start of the consent flow
                    }
                }
            },
            this
        );

        revokeConsentWithToken.save(null, { passwordChallengeToken: passwordToken });
    },

    // User's who have opted out of upload consent before the launch of Revoke Consent Delete (March 2021) and have not yet confirmed whether or not they wish to delete their data need to be shown a warning informing them of their situation
    // The same logic applies for a user who changes their country of residence from a non-EU to an EU country because Garmin is legally required to update their account to be opted out by default when moving an account into the EU from elsewhere
    // No action on the user's part will result in their data being automatically deleted after a grace period
    gdprRcdCheck: function() {
        const deleteStatus = this.gdprFeatureUpload.get('deleteStatus');
        const userOption = this.gdprFeatureUpload.get('userOption');
        const consentEnforcementState = this.gdprFeatureUpload.get('consentEnforcementState');
        const sourceType = this.gdprFeatureUpload.get('sourceType');
        const userIsOptedIn = GdprUtil.isFeatureForUserOn(userOption, consentEnforcementState, sourceType);

        if (
            !userIsOptedIn &&
            (deleteStatus === GdprUtil.DELETE_STATUS.NOT_CONFIRMED ||
                deleteStatus === GdprUtil.DELETE_STATUS.LOCATION_CHANGED ||
                deleteStatus === GdprUtil.DELETE_STATUS.PASSWORD_CHECK_FAILED)
        ) {
            return true;
        }
    },

    // If the user was shown the RCD warning modal before starting the revoke flow, the "Cancel" and "X" (close) buttons in that flow should return the user to the RCD warning modal
    returnToRcdWarningModal: function() {
        return this.gdprRcdCheck() || this.rcdWarningModalShownDueTo406;
    },

    uploadRevokeFirstModal: function(grantOrRevoke, modalTopTitle, consentTypeId) {
        this.consentModalPopupView = this.setGdprModalView(grantOrRevoke, modalTopTitle, consentTypeId);

        const modalPopupView = new ModalPopupView({
            view: this.consentModalPopupView,
            showHeader: false,
            useCustomBody: true,
            showFooter: false,
            onSaveButtonClicked: () => {
                return true;
            },

            // "X" button
            onCloseButtonClicked: () => {
                modalPopupView.close(() => {
                    if (this.returnToRcdWarningModal()) {
                        this.showRcdWarningModal();
                    }
                });
            }
        });

        // "Cancel button"
        modalPopupView.on(
            ModalPopupView.Events.CANCEL_CLICKED,
            function() {
                if (this.returnToRcdWarningModal()) {
                    this.showRcdWarningModal();
                }
            },
            this
        );

        return modalPopupView;
    },

    // Creates a modal with a password verification input
    uploadRevokeSecondModal: function(modalTopTitle, consentTypeId, syncCallback, errorCallback) {
        const that = this;

        const gdprModalDeleteConfirmation = new GdprModalDeleteConfirmation({
            consentType: GdprUtil.CONSENT_TYPE_DATA_DELETION_CONFIRMATION,
            modalTopTitle: modalTopTitle,
            footerPrimaryButtonClasses: 'btn-danger'
        });

        const modalPopupView = new ModalPopupView({
            view: gdprModalDeleteConfirmation,
            useCustomBody: true,
            showHeader: false,
            showFooter: false,
            closable: false,
            onSaveButtonClicked: function() {
                that.onPasswordInput(
                    consentTypeId,
                    syncCallback,
                    errorCallback,
                    gdprModalDeleteConfirmation.getPassword(),
                    function() {
                        gdprModalDeleteConfirmation.onPasswordEntryFail();
                    },
                    function() {
                        gdprModalDeleteConfirmation.onPasswordConfirmFail();
                    },
                    function() {
                        gdprModalDeleteConfirmation.onPasswordConfirmNetworkFail();
                    },
                    function() {
                        gdprModalDeleteConfirmation.onAuthorizationFail();
                    },
                    function() {
                        modalPopupView.close();
                    }
                );
            },
            // "X" button
            onCloseButtonClicked: () => {
                modalPopupView.close(() => {
                    if (this.returnToRcdWarningModal()) {
                        this.showRcdWarningModal();
                    }
                });
            }
        });

        // "Cancel button"
        modalPopupView.on(
            ModalPopupView.Events.CANCEL_CLICKED,
            function() {
                if (this.returnToRcdWarningModal()) {
                    this.showRcdWarningModal();
                }
            },
            this
        );

        return modalPopupView;
    },

    // Confirm the password the user has entered
    onPasswordInput: function(
        consentTypeId,
        syncCallback,
        errorCallback,
        password,
        passwordEntryFail,
        passwordConfirmFail,
        passwordConfirmNetworkFail,
        authorizationFail,
        closeDeleteConfirmation
    ) {
        const checkPasswordWithToken = new CheckPasswordWithToken({
            garminGuid: this.gdprFeatureUpload.get('garminGuid')
        });

        checkPasswordWithToken.verify(
            password,
            function(passwordToken) {
                this.passwordVerified(
                    consentTypeId,
                    syncCallback,
                    function() {
                        errorCallback && errorCallback();
                        passwordConfirmNetworkFail && passwordConfirmNetworkFail();
                    },
                    closeDeleteConfirmation,
                    passwordToken,
                    authorizationFail
                );
            }.bind(this),
            function() {
                passwordEntryFail && passwordEntryFail();
            }.bind(this),
            function() {
                passwordConfirmFail && passwordConfirmFail();
            }.bind(this),
            function() {
                passwordConfirmNetworkFail && passwordConfirmNetworkFail();
            }.bind(this)
        );
    },

    // Once the password is confirmed, revoke the user's consent to start the deletion process
    passwordVerified: function(
        consentTypeId,
        syncCallback,
        errorCallback,
        closeDeleteConfirmation,
        passwordToken,
        authorizationFail
    ) {
        this.revokeConsent(
            consentTypeId,
            function() {
                // If Data Storage is not consented, Garmin Device Upload will be disable
                this.updateAccountDeviceSync('false');
                this.setReactGdprStoreValue(true);
                this.deleteComplete(syncCallback);
                closeDeleteConfirmation && closeDeleteConfirmation();
            }.bind(this),
            errorCallback,
            true,
            passwordToken,
            authorizationFail
        );
    },

    // Creates a confirmation modal to inform the user the deletion process has started after they revoke consent
    deleteComplete: function(syncCallback) {
        const that = this;
        const gdprConfirmationModalView = new GdprConfirmationModalView({
            modalTopTitle: Localizer.localize('your_data_is_being_deleted'),
            modalBody: Localizer.localize('gdpr_revoke_consent_delete_confirmation_2'),
            showFooter: false,
            saveBtn: Localizer.localize('button_ok')
        });

        const modalPopupView = new ModalPopupView({
            view: gdprConfirmationModalView,
            useCustomBody: true,
            showHeader: false,
            showFooter: false,
            // "OK" button
            onSaveButtonClicked: function() {
                modalPopupView.close(() => {
                    if (!!that.navigateToPreviousPage) {
                        Backbone.history.history.back();
                    }
                });
                syncCallback && syncCallback();
            },
            // "X" button
            onCloseButtonClicked: () => {
                modalPopupView.close(() => {
                    if (!!that.navigateToPreviousPage) {
                        Backbone.history.history.back();
                    }
                });
                syncCallback && syncCallback();
            }
        });

        modalPopupView.render();
    },

    // Creates a confirmation modal to inform the user the deletion process is still in progress and to check back later
    dataDeleteInProgress: function() {
        const that = this;
        const gdprConfirmationModalView = new GdprConfirmationModalView({
            modalTopTitle: Localizer.localize('try_again_later'),
            modalBody: Localizer.localize('data_deletion_in_progress'),
            showFooter: false,
            saveBtn: Localizer.localize('button_ok')
        });

        const modalPopupView = new ModalPopupView({
            view: gdprConfirmationModalView,
            useCustomBody: true,
            showHeader: false,
            showFooter: false,
            // "OK" button
            onSaveButtonClicked: function() {
                modalPopupView.close(() => {
                    if (!!that.navigateToPreviousPage) {
                        Backbone.history.history.back();
                    }
                });
            },
            // "X" button
            onCloseButtonClicked: () => {
                modalPopupView.close(() => {
                    if (!!that.navigateToPreviousPage) {
                        Backbone.history.history.back();
                    }
                });
            }
        });

        modalPopupView.render();
    },

    // Creates a take over modal if a user is opted out of GDPR consent and needs to make a choice about keeping or deleting their data
    showRcdWarningModal: function() {
        var that = this;

        this.consentModalPopupView = new GdprModalView({
            consentType: GdprUtil.CONSENT_TYPE_DATA_DELETION,
            grantRevoke: 'grant',
            modalTopTitle: Localizer.localize('data_and_privacy'),
            footerSecondaryButtonClasses: 'btn-danger',
            showCloseButton: false
        });

        // "Keep My Data" button
        var modalPopupView = new ModalPopupView({
            view: this.consentModalPopupView,
            showHeader: false,
            useCustomBody: true,
            showFooter: false,
            closable: false,
            onSaveButtonClicked: function() {
                that.gdprUpload(GdprUtil.RCD_OPTIONS.KEEP);
                modalPopupView.close();
            }
        });

        // "Delete My Data" button
        modalPopupView.on(
            ModalPopupView.Events.CANCEL_CLICKED,
            function() {
                this.gdprUpload(GdprUtil.RCD_OPTIONS.DELETE);
            },
            this
        );

        modalPopupView.render();
    }
});
